<script setup>
import routes from '@/constants/routes';

defineProps({
  className: {
    type: String,
    default: undefined,
  },
});

const route = useRoute();
const localeRoute = useLocaleRoute();
const authStore = useAuthStore();

// breakpoint
const breakpoint = useCustomBreakpoints();
const breakpointDownSm = breakpoint.smaller('sm');

const routesWithoutMobileForm = [routes.SEARCH, routes.CART, routes.MY_TICKETS];
const isRouteWithoutMobileForm = computed(() =>
  routesWithoutMobileForm.some((routePath) =>
    route.fullPath.includes(routePath),
  ),
);

const hideMobileForm = computed(
  () => isRouteWithoutMobileForm.value && breakpointDownSm.value,
);

// Scroll handling
const headerCollapseStore = useHeaderCollapseStore();

let lastScrollY = 0;
const isScrollingDown = ref(false);
const showStickyButtons = ref(false);

const isFormVisible = ref(true);
const isHeadingVisible = ref(true);

let formScrollValue = 608;
let dateSliderStickyValue = 200;

let hideHeaderScrollValue;

watch(
  () => route.name,
  (routeName) => {
    switch (routeName) {
      case localeRoute('index').name:
        hideHeaderScrollValue = toResizedPx(405);
        break;
      case localeRoute('search').name:
        hideHeaderScrollValue = toResizedPx(60);
        break;
      default:
        hideHeaderScrollValue = toResizedPx(100);
    }
  },
  { immediate: true },
);

onMounted(() => {
  lastScrollY = window.scrollY;
  formScrollValue = toResizedPx(formScrollValue);
  dateSliderStickyValue = toResizedPx(dateSliderStickyValue);

  handleScroll();
});
useEventListener('scroll', handleScroll);

function handleScroll() {
  const currentScrollY = window.scrollY;

  isScrollingDown.value =
    currentScrollY > lastScrollY &&
    !breakpointDownSm.value &&
    hideHeaderScrollValue < currentScrollY;

  headerCollapseStore.updateHeaderState(isScrollingDown.value);
  headerCollapseStore.updateDateSliderState(
    currentScrollY > lastScrollY && dateSliderStickyValue - 10 < currentScrollY,
  );
  headerCollapseStore.updateDateSliderStickyState(
    currentScrollY >= dateSliderStickyValue,
  );

  lastScrollY = currentScrollY;

  isFormVisible.value = currentScrollY <= formScrollValue;
  isHeadingVisible.value = !isRouteWithoutMobileForm.value;
  showStickyButtons.value = window.scrollY > 200;
}

function handleScrollButtonClick() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>

<template>
  <div class="layouts-search" :class="className">
    <LayoutHeader
      class="layouts-search__header"
      :class="{ 'layouts-search__header--scrolled': isScrollingDown }"
      :is-form-visible="isFormVisible"
      :is-heading-visible="isHeadingVisible"
    />

    <BlocksPageHeading
      v-show="!hideMobileForm"
      class="layouts-search__heading"
      :class="{
        'layouts-search__heading--vertical-paddings': !!route.meta.titleKey,
      }"
      :title-key="route.meta.titleKey"
      :text-key="route.meta.textKey"
    />

    <LayoutFormSearch
      v-show="!hideMobileForm"
      class="layouts-search__form"
      :class="{ 'layouts-search__form--scrolled': isScrollingDown }"
    />

    <div class="layouts-search__container">
      <div class="layouts-search__wrapper layouts-search__wrapper--left">
        <Transition name="fade">
          <CLocalizedLink
            v-if="
              route.meta.showStickyButtons &&
              showStickyButtons &&
              !!authStore.account?.activeCartTicketsCount &&
              breakpointDownSm
            "
            :to="routes.CART"
          >
            <LabelsStatusCircle
              class="layouts-search__button layouts-search__button--cart"
              :class="{
                'layouts-search__button--shifted':
                  route.meta.shiftStickyButtons,
              }"
              :has-status="!!authStore.account?.activeCartTicketsCount"
              :count="authStore.account?.activeCartTicketsCount"
            >
              <CIcon
                class="layouts-search__icon layouts-search__icon--cart"
                name="cart"
              />
            </LabelsStatusCircle>
          </CLocalizedLink>
        </Transition>
      </div>

      <slot class-name="layouts-search__view" />

      <div class="layouts-search__wrapper layouts-search__wrapper--right">
        <Transition name="fade">
          <button
            v-if="route.meta.showStickyButtons && showStickyButtons"
            type="button"
            class="layouts-search__button layouts-search__button--arrow"
            :class="{
              'layouts-search__button--shifted': route.meta.shiftStickyButtons,
            }"
            @click="handleScrollButtonClick"
          >
            <CIcon
              class="layouts-search__icon layouts-search__icon--arrow"
              name="arrow-3"
            />
          </button>
        </Transition>
      </div>
    </div>

    <LayoutFooter
      class="layouts-search__footer"
      :hide-mobile-footer-content="route.meta.hideMobileFooter"
      :with-contacts-gap="route.meta.withContactsGap"
      :show-contacts="!route.meta.hideFooterContacts"
    />
  </div>
</template>

<style scoped lang="scss">
.layouts-search {
  $parent: &;

  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__icon {
    flex-shrink: 0;

    &--cart {
      @include box(em(24));
    }

    &--arrow {
      @include box(em(24));

      transform: rotate(-90deg);
    }
  }

  &__header {
    position: sticky;
    top: 0;
    right: 0;
    left: 0;
    z-index: 100;
    height: em($header-height);
    transition: top $time-5;

    @include media-breakpoint-down(sm) {
      position: relative;
      top: em(10);
      right: em($gap-sm);
      left: em($gap-sm);
      width: calc(100% - 2 * #{em($gap-sm)});
      height: em($header-height-sm);
    }

    &--scrolled {
      top: em(-$header-height);
    }
  }

  &__heading {
    @include media-breakpoint-down(sm) {
      padding: em(49 + $header-height-sm) 0 em(29);
      margin-top: em(-$header-height-sm);
    }

    &--vertical-paddings {
      @include media-breakpoint-up(sm) {
        padding: em(80) 0 em(44);
      }
    }
  }

  &__form {
    @include media-breakpoint-up(sm) {
      position: sticky;
      top: em($header-height);
      z-index: 99;
      transition: top $time-5;
    }

    @include media-breakpoint-down(sm) {
      min-height: em(377);
    }

    &--scrolled {
      top: 0;
    }
  }

  &__container {
    position: relative;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
  }

  &__wrapper {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 3;
    width: em(40);
    padding-top: em(400);
    pointer-events: none;

    &--left {
      left: em($gap-sm);
      padding-bottom: em(70);
    }

    &--right {
      right: em($gap-sm);
      padding-bottom: em(24);

      @include media-breakpoint-down(sm) {
        padding-bottom: em(70);
      }
    }
  }

  &__button {
    @include box(em(40));

    position: sticky;
    top: calc(100% - #{em(60)});
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    border-radius: 50%;

    &--cart {
      color: $color-second-1;
      background-color: $color-main-1;
    }

    &--arrow {
      color: $color-white;
      background-color: $color-second-1;
    }

    &--shifted {
      top: calc(100% - #{em(140)});
    }
  }

  &__footer {
    margin-top: auto;
  }
}
</style>
